/* eslint-disable react/no-unknown-property */

import { Html, Line } from '@react-three/drei'
import { useMemo } from 'react'
import { ConeGeometry, CylinderGeometry, Vector3 } from 'three'

// Values that define the arrow head tip shape
const arrowHeadTipRadius = 1.25
const arrowHeadTipLength = 2
const arrowHeadTipPaddingFromReference = 0.5

// Values that define the arrow trunk shape
const arrowTrunkRadius = 0.5

// Values that define line width
const lineWidth = 2

// Defines the position of the label along the arrow, starting from
// the origin. 0.8 is 80% of the way along the arrow
const arrowLabelPlacementMultiplier = 0.5

type PropTypes = {
  points: [number, number, number][]
  referenceLinePoints: [[number, number, number], [number, number, number]][]
  label?: string
  color?: string | number
}

const DistanceArrow3D = ({ points, referenceLinePoints, label, color = 'black' }: PropTypes) => {
  const labelPoint = useMemo(() => {
    const lastReferencePoint = referenceLinePoints[referenceLinePoints.length - 1][1]
    const firstReferencePoint = referenceLinePoints[0][1]

    const lastPointVector = new Vector3(...lastReferencePoint)
    const firstPointVector = new Vector3(...firstReferencePoint)

    // Subtracts the end from the start to get the direction of the line, not normalised as we want the length.
    // Then we multiply by the placement multiplier to get a point along the total length of the line
    const pointAlongLine = lastPointVector.clone().sub(firstPointVector).multiplyScalar(arrowLabelPlacementMultiplier)

    // Get the point relative to the end of the arrow, rather than the origin
    const labelPoint = lastPointVector.clone().sub(pointAlongLine.clone())

    return labelPoint.toArray() as [number, number, number]
  }, [referenceLinePoints])

  const endArrowTipModel = useMemo(() => {
    const lastPoint = points[points.length - 1]
    const firstPoint = points[0]

    const lastPointVector = new Vector3(...lastPoint)
    const firstPointVector = new Vector3(...firstPoint)

    // Calculate a direction vector from the last point to the first point
    const direction = lastPointVector.clone().sub(firstPointVector).normalize()
    const distance = lastPointVector.distanceTo(firstPointVector)

    // Tip length should never be longer than the arrow itself
    const tipLength = distance > arrowHeadTipLength ? arrowHeadTipLength : distance

    const point = new ConeGeometry(arrowHeadTipRadius, tipLength)

    // Get an origin for the cone model, half it's own length off the end of the line
    const origin = direction.clone().multiplyScalar(-((tipLength / 2) + arrowHeadTipPaddingFromReference))

    // First rotate the point, as the lookAt function orients the positive Z axis towards the target
    point.rotateX(Math.PI / 2)
    point.lookAt(direction)

    // Translate the point to the end of the line
    point.translate(lastPointVector.x, lastPointVector.y, lastPointVector.z)

    // Further translate the point by half it's length off the end of the line to remove overlaps
    point.translate(origin.x, origin.y, origin.z)

    return point
  }, [points])

  const startArrowTipModel = useMemo(() => {
    const lastPoint = points[points.length - 1]
    const firstPoint = points[0]

    const lastPointVector = new Vector3(...lastPoint)
    const firstPointVector = new Vector3(...firstPoint)

    // Calculate a direction vector from the last point to the first point
    const direction = firstPointVector.clone().sub(lastPointVector).normalize()
    const distance = firstPointVector.distanceTo(lastPointVector)

    // Tip length should never be longer than the arrow itself
    const tipLength = distance > arrowHeadTipLength ? arrowHeadTipLength : distance

    const point = new ConeGeometry(arrowHeadTipRadius, tipLength)

    // Get an origin for the cone model, half it's own length off the end of the line
    const origin = direction.clone().multiplyScalar(-((tipLength / 2) + arrowHeadTipPaddingFromReference))

    // First rotate the point, as the lookAt function orients the positive Z axis towards the target
    point.rotateX(Math.PI / 2)
    point.lookAt(direction)

    // Translate the point to the end of the line
    point.translate(firstPointVector.x, firstPointVector.y, firstPointVector.z)

    // Further translate the point by half it's length off the end of the line to remove overlaps
    point.translate(origin.x, origin.y, origin.z)

    return point
  }, [points])

  const arrowHeadTrunkModel = useMemo(() => {
    const lastPoint = points[points.length - 1]
    const firstPoint = points[0]

    const lastPointVector = new Vector3(...lastPoint)
    const firstPointVector = new Vector3(...firstPoint)

    // Calculate a direction vector from the last point to the second last point
    const direction = lastPointVector.clone().sub(firstPointVector).normalize()
    const distance = lastPointVector.distanceTo(firstPointVector)

    const point = new CylinderGeometry(arrowTrunkRadius, arrowTrunkRadius, distance - arrowHeadTipLength - arrowHeadTipPaddingFromReference)

    // Get an origin for the cone model, half it's own length away from the end of the line
    const origin = direction.clone().multiplyScalar(-(distance / 2))

    // First rotate the point, as the lookAt function orients the positive Z axis towards the target
    point.rotateX(Math.PI / 2)
    point.lookAt(direction)

    // Translate the point to the end of the line
    point.translate(lastPointVector.x, lastPointVector.y, lastPointVector.z)

    // Further translate the point by half it's length away from the end of the line to remove overlaps
    point.translate(origin.x, origin.y, origin.z)

    return point
  }, [points])

  return (
    <group>
      <mesh>
        <primitive object={endArrowTipModel} />
        <meshStandardMaterial color={color} />
      </mesh>
      <mesh>
        <primitive object={startArrowTipModel} />
        <meshStandardMaterial color={color} />
      </mesh>
      <mesh>
        <primitive object={arrowHeadTrunkModel} />
        <meshStandardMaterial color={color} />
      </mesh>
      {
        referenceLinePoints && referenceLinePoints.map(([start, end], index) => (
          <Line
            key={`arrow-${start.join(',')}-${end.join(',')}-${index}`}
            points={[start, end]}
            color={color}
            lineWidth={lineWidth}
          />
        ))
      }
      <Html
        position={labelPoint}
        center
      >
        <div
          className="bg-white bg-opacity-90 px-1 rounded cursor-default select-none text-xs text-nowrap"
        >
          {label}
        </div>
      </Html>
    </group>
  )
}

export default DistanceArrow3D
